import React from 'react';
import { IContact } from 'src/api/models/Organization';
import { getErrorMessage, logger, showErrorToast } from 'src/utils';

const DEBOUNCE_MS = 400;

/**
 * @registry Fetches a workflow log PDF for the open log modal, refetching as the status/contact filters change. The first fetch after opening fires immediately; later filter changes are debounced (~400ms) so rapid MultiSelect ticks collapse into one server render. Stale responses are dropped and failures toast + invoke onError.
 */
export function useWorkflowLogPdf<TFile>({
  open,
  jobId,
  statuses,
  contact,
  fetchPdf,
  logLabel,
  onError,
}: {
  open: boolean;
  jobId: number | undefined;
  statuses: string[];
  contact: IContact | null;
  fetchPdf: (
    jobId: number,
    statuses: string[] | null,
    contactId: string | null,
  ) => Promise<TFile | null>;
  /** Message logged when the render fails. */
  logLabel: string;
  /** Invoked on failure so the caller can close the log modal. */
  onError?: () => void;
}) {
  const [file, setFile] = React.useState<TFile | null>(null);
  const [loading, setLoading] = React.useState(false);
  const firstLoad = React.useRef(true);

  const fetchRef = React.useRef(fetchPdf);
  fetchRef.current = fetchPdf;
  const onErrorRef = React.useRef(onError);
  onErrorRef.current = onError;
  const logLabelRef = React.useRef(logLabel);
  logLabelRef.current = logLabel;

  React.useEffect(() => {
    if (!open) {
      firstLoad.current = true;
      setFile(null);
    }
  }, [open]);

  React.useEffect(() => {
    if (!open || jobId == null) return;
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        const result = await fetchRef.current(
          jobId,
          statuses.length ? statuses : null,
          contact?.id ?? null,
        );
        if (cancelled) return;
        setFile(result);
      } catch (err: unknown) {
        if (cancelled) return;
        logger.error(logLabelRef.current, { err });
        showErrorToast(getErrorMessage(err));
        onErrorRef.current?.();
      }
      if (cancelled) return;
      setLoading(false);
    };

    if (firstLoad.current) {
      firstLoad.current = false;
      run();
      return () => {
        cancelled = true;
      };
    }

    const timer = setTimeout(run, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, jobId, statuses, contact]);

  return { file, loading };
}
